import React from 'react';

import { useRouter } from 'next/router';
import { setSsrTokens } from 'utils/authentication';
import { queryClientConfig } from '@core/configs/queryClientConfig';
import { useQuery, useMutation, dehydrate, QueryClient } from 'react-query';
import { CareerPath } from 'services/core';
import UseToast from 'hooks/use-toast';
import { getErrorText } from 'utils/geterrortext';
import { GetCareerPathCertificate } from 'services/core';
import { downloadFile } from 'utils/fileConvert';
import { PROFILE_DETAIL_ERROR } from '@core/constants/Errors';

import { NextSeo } from 'next-seo';
import { getCareerPathDynamicProps } from 'router/seoprops';

import BackButton from '@core/components/backbutton/BackButton';
import CareerCourseCard from 'components/pages/career/careercoursecard/CareerCourseCard';
import ActionButton from '@core/components/actionbutton/ActionButton';
import Spinner from '@core/components/spinner/Spinner';

import CareerPathCertificateImage from 'assets/images/pages/career-path/Career-Path-Certificate.png';

import classes from './UserCareerPath.module.scss';


const UserCareerPath = (props) => {
    const seoProps = getCareerPathDynamicProps({ ...props });
    const { ToastContainer, toast } = UseToast();
    const router = useRouter();
    const { data, isLoading, refetch } = useQuery(
        ['get-career-path', router?.query?.slug[0]],
        () => CareerPath.GET(router?.query?.slug[0]),
        {
            cacheTime: 0,
            staleTime: 0,
            enabled: !!router?.query?.slug[0]
        }
    );
    const { mutate } = useMutation(['enroll-career-path', router?.query?.slug[0]], () => CareerPath.POST(router?.query?.slug[0]), {
        onSuccess: (data) => {
            refetch();
            toast(data?.data?.message);
        },
        onError: (error) => {
            if (error.response.status === 401) {
                router.push('/login?next=/career-path/' + router?.query?.slug[0]);
            }
            else
                toast(getErrorText(error), 'alert');
        }
    });

    const { data: certificatePDF, isFetching, refetch: refetchCareerPathCertificate } = useQuery(['career-path-certificate'], () => GetCareerPathCertificate(router?.query?.slug[0]),
        {
            onSuccess: (certificatePDF) => {
                downloadFile(certificatePDF);
            },
            onError: (error) => {
                if (error?.response?.status === +PROFILE_DETAIL_ERROR) {
                    // showFullnameModal();
                    toast(
                        'Please fill in your full name to generate the certificate.',
                        'alert'
                    );
                }
                else
                    toast(
                        'There is a problem on the Certificare generation. Please try later.',
                        'alert'
                    );
            },
            cacheTime: 0,
            enabled: false,
        });

    return (
        <>
            <NextSeo {...seoProps} />
            <ToastContainer />

            <div className='container'>
                <div className='row justify-content-center mx-1 mt-5'>
                    {/* <div className={classes['back-button']}>
                        <BackButton type='bordered' />
                        Career Paths
                    </div> */}
                    {(data && !isLoading) ? (
                        <>
                            <div
                                className={classes['header']}
                                style={{ backgroundImage: `linear-gradient(270deg, rgba(217, 217, 217, 0.00) -10%, #FFF 38.21%),url(${data.data.image})` }}
                            >
                                <h2>{data.data.name}</h2>
                                <p>
                                    {data.data.description}
                                </p>
                                <div className={classes['info']}>
                                    {data.data.estimated_duration && (
                                        <div>
                                            <span>
                                                Estimated path length
                                            </span>
                                            <h4>
                                                {data.data.estimated_duration}
                                            </h4>
                                        </div>
                                    )}
                                    {data.data.note && (
                                        <div>
                                            <span>
                                                Note:
                                            </span>
                                            <h4>
                                                {data.data.note}
                                            </h4>
                                        </div>
                                    )}
                                </div>
                                {/* <span>{data.data.lecture_count} Courses</span> */}
                                {data.data.is_enrolled ? (
                                    <div className={classes['enrolled']}>
                                        You’ve started this path
                                    </div>
                                ) : (
                                    <ActionButton onClick={() => mutate()}>
                                        Enroll Path
                                    </ActionButton>
                                )}
                            </div>
                            <div className='row mb-5'>
                                <div className='col-lg-9 col-12'>
                                    <div className={classes['courses']}>
                                        {data.data.lectures.map((content) => (
                                            <CareerCourseCard data={content} />
                                        ))}
                                    </div>
                                </div>
                                <div className='col-lg-3 col-12 '>
                                    <div className={classes['certificate-wrapper']}>
                                        <img src={CareerPathCertificateImage.src} alt='Career Path Certificate' />
                                        <h4>
                                            Pharmuni Certificate
                                        </h4>
                                        <p>
                                            Developed by Zamann Pharma Support, a TÜV ISO 9001-certified quality
                                            management service provider for European pharmaceutical companies,
                                        </p>
                                        <ActionButton onClick={() => refetchCareerPathCertificate()} disabled={!data.data.is_completed} loading={isFetching} >
                                            Get Certificate
                                        </ActionButton>
                                    </div>
                                </div>
                            </div>

                        </>
                    ) : (
                        <div
                            className={`${classes['loading']} d-flex flex-row align-items-center justify-content-center`}>
                            <Spinner />
                        </div>
                    )}
                </div>
            </div >
        </>
    );
};



export async function getServerSideProps(context) {
    setSsrTokens(context);
    const queryClient = new QueryClient(queryClientConfig);
    const careerID = context?.params?.slug[0];
    try {
        const response = await CareerPath.GET(careerID);
        await queryClient.prefetchQuery(['get-career-path', careerID], () => ({ data: response.data }));
        let newQueryParams = '';
        const { slug, ...queryParams } = context.query;
        if (queryParams && Object.keys(queryParams).length > 0) {
            newQueryParams = `?${new URLSearchParams(queryParams).toString()}`;
        }
        if (!context?.params?.slug[1]) {
            context.res.setHeader(
                'Location',
                `/career-path/${context?.params?.slug[0]}/${response.data.slug}${newQueryParams}`
            );
            context.res.statusCode = 302;
            context.res.end();
        }
        return {
            props: {
                dehydratedState: dehydrate(queryClient),
                id: careerID,
                title: response.data.name,
                description: response.data.description,
                slug: response.data.slug
            }
        };
    } catch (e) {
        if (e.response.status === 404)
            return {
                redirect: {
                    permanent: false,
                    destination: "/404"
                }
            }
        else
            return {}
    }
}

export default UserCareerPath;